import React from "react";
import styled from "styled-components";

function percentparseFloat(num) {
  return num % 1 === 0 ? num : num.toFixed(2);
}

const TimeSheetOverView = ({ timeSheet }) => {
  const chargeableOverview = getChargeableOverview(timeSheet);
  const nonChargeableOverview = getNonChargeableOverview(timeSheet);
  const approvedAbsenceOverview = getApprovedAbsenceOverview(timeSheet);

  const totalTasks =
    chargeableOverview.totalTask +
    nonChargeableOverview.totalTask +
    approvedAbsenceOverview.totalTask;

  const totalHours =
    chargeableOverview.totalHours +
    nonChargeableOverview.totalHours +
    approvedAbsenceOverview.totalHours;

  const totalChargeableHours = chargeableOverview.totalHours;
  const totalNonChargeableHours = nonChargeableOverview.totalHours;
  const totalAbsenceHours = approvedAbsenceOverview.totalHours;

  // Effective Working Hours = Total Hours - Absence/Holiday Hours
  const effectiveTotalHours = totalHours - totalAbsenceHours;

  const chargeablePercentage =
    effectiveTotalHours !== 0
      ? (totalChargeableHours / effectiveTotalHours) * 100
      : 0;

  const utilizationPercentage =
    effectiveTotalHours !== 0
      ? ((totalChargeableHours + totalNonChargeableHours) /
        effectiveTotalHours) *
      100
      : 0;

  const summaryRows = [
    {
      label: "Chargeable (C)",
      tasks: chargeableOverview.totalTask,
      hours: chargeableOverview.totalHours,
      color: "#2f7d5c",
    },
    {
      label: "Non-Chargeable (NC)",
      tasks: nonChargeableOverview.totalTask,
      hours: nonChargeableOverview.totalHours,
      color: "#6b7ba8",
    },
    {
      label: "Absence (AP)",
      tasks: approvedAbsenceOverview.totalTask,
      hours: approvedAbsenceOverview.totalHours,
      color: "#e0475a",
    },
  ];

  return (
    <TimeSheetOverViewWrap>
      <div className="wsc-title">Weekly Summary</div>

      <div className="wsc-total-box">
        <div className="wsc-total-item">
          <span className="wsc-total-label">Total Task</span>
          <span className="wsc-total-value">{percentparseFloat(totalTasks)}</span>
        </div>
        <div className="wsc-total-item wsc-total-item-right">
          <span className="wsc-total-label">Total Hours</span>
          <span className="wsc-total-value">{percentparseFloat(totalHours)}</span>
        </div>
      </div>

      <div className="wsc-rows">
        {summaryRows.map((row) => (
          <div className="wsc-row" key={row.label}>
            <span className="wsc-row-bar" style={{ background: row.color }} />
            <div className="wsc-row-text">
              <span className="wsc-row-label">{row.label}</span>
              <span className="wsc-row-sub">{row.tasks} Task{row.tasks === 1 ? "" : "s"}</span>
            </div>
            <span className="wsc-row-hours">{percentparseFloat(row.hours)}h</span>
          </div>
        ))}
      </div>

      <div className="wsc-metric">
        <div className="wsc-metric-header">
          <span className="wsc-metric-title">Chargeability %</span>
          <span className="wsc-metric-value">{percentparseFloat(chargeablePercentage)}%</span>
        </div>
        <div className="wsc-metric-formula">C / Effective Working Hours</div>
        <div className="wsc-progress-track">
          <div
            className="wsc-progress-fill"
            style={{ width: `${Math.min(chargeablePercentage, 100)}%` }}
          />
        </div>
      </div>

      <div className="wsc-metric">
        <div className="wsc-metric-header">
          <span className="wsc-metric-title">Utilization %</span>
          <span className="wsc-metric-value">{percentparseFloat(utilizationPercentage)}%</span>
        </div>
        <div className="wsc-metric-formula">(C + NC) / Effective Working Hours</div>
        <div className="wsc-progress-track">
          <div
            className="wsc-progress-fill"
            style={{ width: `${Math.min(utilizationPercentage, 100)}%` }}
          />
        </div>
      </div>
    </TimeSheetOverViewWrap>
  );
};

const TimeSheetOverViewWrap = styled.div`
  width: 340px;
  background: #ffffff;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.12);
  font-family: "DM Sans", sans-serif;

  .wsc-title {
    font-size: 18px;
    font-weight: 700;
    color: #1f243b;
    margin-bottom: 14px;
  }

  .wsc-total-box {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #e3f2ee;
    border-radius: 12px;
    padding: 14px 18px;
    margin-bottom: 18px;
  }

  .wsc-total-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .wsc-total-item-right {
    align-items: flex-end;
    text-align: right;
  }

  .wsc-total-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.5px;
    color: #5b6572;
    text-transform: uppercase;
  }

  .wsc-total-value {
    font-size: 26px;
    font-weight: 700;
    color: #00685f;
  }

  .wsc-rows {
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-bottom: 18px;
  }

  .wsc-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .wsc-row-bar {
    width: 4px;
    height: 30px;
    border-radius: 2px;
    flex-shrink: 0;
  }

  .wsc-row-text {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  .wsc-row-label {
    font-size: 14px;
    font-weight: 600;
    color: #1f243b;
  }

  .wsc-row-sub {
    font-size: 12px;
    color: #8a93a3;
  }

  .wsc-row-hours {
    font-size: 15px;
    font-weight: 700;
    color: #1f243b;
  }

  .wsc-metric {
    margin-bottom: 14px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .wsc-metric-header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 2px;
  }

  .wsc-metric-title {
    font-size: 14px;
    font-weight: 600;
    color: #1f243b;
  }

  .wsc-metric-value {
    font-size: 13px;
    font-weight: 700;
    color: #1f243b;
  }

  .wsc-metric-formula {
    font-size: 11px;
    color: #8a93a3;
    margin-bottom: 8px;
  }

  .wsc-progress-track {
    width: 100%;
    height: 6px;
    border-radius: 3px;
    background: #e4e4e8;
    overflow: hidden;
  }

  .wsc-progress-fill {
    height: 100%;
    border-radius: 3px;
    background: linear-gradient(90deg, #06c270, #06c270);
  }
`;

export default TimeSheetOverView;

const getChargeableOverview = (timeSheet) => {
  const totalTask = timeSheet.reduce((acc, item) => {
    if (item.task && item.client !== "nc" && item.client !== "ap") {
      return acc + 1;
    }
    return acc;
  }, 0);

  const totalHours = timeSheet.reduce((acc, item) => {
    if (item.client !== "nc" && item.client !== "ap") {
      return (
        acc +
        (parseFloat(item.mon) || 0) +
        (parseFloat(item.tue) || 0) +
        (parseFloat(item.wed) || 0) +
        (parseFloat(item.thu) || 0) +
        (parseFloat(item.fri) || 0) +
        (parseFloat(item.sat) || 0) +
        (parseFloat(item.sun) || 0)
      );
    }
    return acc;
  }, 0);

  return {
    totalTask: parseFloat(totalTask),
    totalHours: parseFloat(totalHours),
  };
};

const getNonChargeableOverview = (timeSheet) => {
  const totalTask = timeSheet.reduce((acc, item) => {
    if (item.task && item.client === "nc") {
      return acc + 1;
    }
    return acc;
  }, 0);

  const totalHours = timeSheet.reduce((acc, item) => {
    if (item.client === "nc") {
      return (
        acc +
        (parseFloat(item.mon) || 0) +
        (parseFloat(item.tue) || 0) +
        (parseFloat(item.wed) || 0) +
        (parseFloat(item.thu) || 0) +
        (parseFloat(item.fri) || 0) +
        (parseFloat(item.sat) || 0) +
        (parseFloat(item.sun) || 0)
      );
    }
    return acc;
  }, 0);

  return {
    totalTask: parseFloat(totalTask),
    totalHours: parseFloat(totalHours),
  };
};

const getApprovedAbsenceOverview = (timeSheet) => {
  const totalTask = timeSheet.reduce((acc, item) => {
    if (item.task && item.client === "ap") {
      return acc + 1;
    }
    return acc;
  }, 0);

  const totalHours = timeSheet.reduce((acc, item) => {
    if (item.client === "ap") {
      return (
        acc +
        (parseFloat(item.mon) || 0) +
        (parseFloat(item.tue) || 0) +
        (parseFloat(item.wed) || 0) +
        (parseFloat(item.thu) || 0) +
        (parseFloat(item.fri) || 0) +
        (parseFloat(item.sat) || 0) +
        (parseFloat(item.sun) || 0)
      );
    }
    return acc;
  }, 0);

  return {
    totalTask: parseFloat(totalTask),
    totalHours: parseFloat(totalHours),
  };
};